import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useStore } from "@/lib/pos-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Check, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { hasRole } from "@/lib/auth-store";

export const Route = createFileRoute("/receipt/$id")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "viewer")) {
      throw redirect({ to: "/login" });
    }
  },
  head: () => ({
    meta: [
      { title: "Receipt" },
      { name: "description", content: "Sale receipt." },
    ],
  }),
  component: ReceiptPage,
});

/** The receipt content only — shared for both screen and print. */
function buildReceiptHtml(
  receiptSales: any[],
  firstSale: any,
  grandTotal: number,
  state: any,
) {
  const fmtHtml = (n: number) =>
    new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      maximumFractionDigits: 0,
    }).format(n);

  const itemsHtml = receiptSales
    .map((s: any) => {
      const item = state.items.find((i: any) => i.id === s.itemId);
      if (!item) return "";
      return `
        <tr>
          <td style="padding:2px 0;">${item.name}<br><span style="color:#888;font-size:10px;">${s.qty} × ${fmtHtml(s.unitPrice)}</span></td>
          <td style="text-align:right;padding:2px 0;white-space:nowrap;">${fmtHtml(s.total)}</td>
        </tr>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Receipt</title>
  <style>
    @page { margin: 0; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      margin: 0;
      padding: 32px 16px 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      background: white;
      color: black;
    }
    .receipt-body {
      width: 80mm;
      max-width: 100%;
    }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .line { border-top: 1px dashed #999; margin: 8px 0; }
    table { width: 100%; border-collapse: collapse; }
    td:last-child { text-align: right; white-space: nowrap; }
    .total td { padding-top: 4px; font-size: 14px; font-weight: bold; }
  </style>
</head>
<body>
  <div class="receipt-body">
    <div class="center bold" style="font-size:14px;">White House Lounge</div>
    <div class="center" style="color:#888;font-size:10px;">POS · Receipt #${firstSale.id.slice(-6).toUpperCase()}</div>
    <div class="line"></div>
    <div>Date: ${new Date(Number(firstSale.at)).toLocaleString()}</div>
    <div>Payment: <span style="text-transform:uppercase">${firstSale.payment}</span></div>
    <div class="line"></div>
    <table>
      <tr class="bold"><td>Item</td><td>Total</td></tr>
      ${itemsHtml}
    </table>
    <div class="line"></div>
    <table>
      <tr class="total"><td>TOTAL</td><td>${fmtHtml(grandTotal)}</td></tr>
    </table>
    <div class="center" style="color:#888;font-size:10px;margin-top:16px;">Thank you · Karibu tena</div>
  </div>
</body>
</html>`;
}

function ReceiptPage() {
  const { id } = Route.useParams();
  const state = useStore((s) => s);

  // Find the sale by id or by receiptId (checkout returns receiptId)
  const sale =
    state.sales.find((s) => s.id === id) ||
    state.sales.find((s) => s.receiptId === id);
  if (!sale) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <p className="text-sm font-medium text-muted-foreground">
          Receipt not found.
        </p>
        <Link
          to="/"
          className="mt-3 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground min-h-[44px] flex items-center"
        >
          Go home
        </Link>
      </div>
    );
  }

  // Determine the receipt ID to group by
  const effectiveReceiptId = sale.receiptId || id;
  // Find all sales sharing the same receiptId (grouped transaction)
  const receiptSales = state.sales.filter(
    (s) => s.receiptId === effectiveReceiptId,
  );
  // Use the correct set of sales for display
  const displaySales = receiptSales.length > 0 ? receiptSales : [sale];

  const grandTotal = displaySales.reduce((a, s) => a + s.total, 0);
  const firstSale = displaySales[0];

  const handlePrint = () => {
    const printWin = window.open("", "_blank", "width=400,height=600");
    if (!printWin) return;
    printWin.document.write(
      buildReceiptHtml(displaySales, firstSale, grandTotal, state),
    );
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  const receiptItems = displaySales.map((s) => {
    const item = state.items.find((i: any) => i.id === s.itemId);
    return { ...s, itemName: item?.name ?? "—" };
  });

  return (
    <div>
      <PageHeader title="Receipt" />
      <div className="px-4 pt-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-7 w-7" strokeWidth={3} />
        </div>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Sale completed
        </p>
        <p className="mt-1 text-center font-display text-3xl font-bold">
          {fmt(grandTotal)}
        </p>
      </div>

      <div className="mx-4 mt-6 rounded-xl border-2 border-dashed border-border bg-card p-5 font-mono text-xs">
        <p className="text-center font-display text-base font-semibold">
          White House Lounge
        </p>
        <p className="text-center text-[10px] text-muted-foreground">
          POS · Receipt #{firstSale.id.slice(-6).toUpperCase()}
        </p>
        <div className="my-3 border-t border-dashed border-border" />
        <div className="flex justify-between">
          <span>Date</span>
          <span>{new Date(Number(firstSale.at)).toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span>Payment</span>
          <span className="uppercase">{firstSale.payment}</span>
        </div>
        <div className="my-3 border-t border-dashed border-border" />
        <div className="flex justify-between font-semibold">
          <span>Item</span>
          <span>Total</span>
        </div>
        {receiptItems.map((s) => (
          <div key={s.id} className="mt-1 flex justify-between">
            <span>
              {s.itemName}
              <br />
              <span className="text-muted-foreground">
                {s.qty} × {fmt(s.unitPrice)}
              </span>
            </span>
            <span>{fmt(s.total)}</span>
          </div>
        ))}
        <div className="my-3 border-t border-dashed border-border" />
        <div className="flex justify-between text-base font-bold">
          <span>TOTAL</span>
          <span>{fmt(grandTotal)}</span>
        </div>
        <p className="mt-4 text-center text-[10px] text-muted-foreground">
          Thank you · Karibu tena
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 px-4 pt-4">
        <Button variant="secondary" onClick={handlePrint}>
          <Printer className="h-4 w-4" /> Print
        </Button>
        <Link to="/sell">
          <Button className="w-full">New Sale</Button>
        </Link>
      </div>
    </div>
  );
}
